// Validações centralizadas para o app
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

// Validação de email
export function validateEmail(email: string): ValidationResult {
  const errors: string[] = [];
  
  if (!email || !email.trim()) {
    errors.push("Email é obrigatório");
    return { isValid: false, errors };
  }
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    errors.push("Email deve ter um formato válido");
  }
  
  return { isValid: errors.length === 0, errors };
}

// Função para limpar telefone (remover caracteres não numéricos)
export function cleanPhone(phone: string): string {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
}

// Validação de telefone
export function validatePhone(phone: string): ValidationResult {
  const errors: string[] = [];
  
  if (!phone || !phone.trim()) {
    errors.push("Telefone é obrigatório");
    return { isValid: false, errors };
  }
  
  // Remove todos os caracteres não numéricos
  const cleanPhoneNumber = cleanPhone(phone);
  
  if (cleanPhoneNumber.length < 10) {
    errors.push("Telefone deve ter pelo menos 10 dígitos");
  }
  
  if (cleanPhoneNumber.length > 14) {
    errors.push("Telefone deve ter no máximo 14 dígitos");
  }
  
  // Validação específica para telefones brasileiros
  if (cleanPhoneNumber.length === 11 && !cleanPhoneNumber.startsWith('11')) {
    // Telefone celular com DDD diferente de 11 (SP)
    if (!cleanPhoneNumber.match(/^[1-9][1-9]9[0-9]{8}$/)) {
      errors.push("Formato de telefone celular inválido");
    }
  } else if (cleanPhoneNumber.length === 10) {
    // Telefone fixo
    if (!cleanPhoneNumber.match(/^[1-9][1-9][0-9]{8}$/)) {
      errors.push("Formato de telefone fixo inválido");
    }
  } else if (cleanPhoneNumber.length === 12 || cleanPhoneNumber.length === 13) {
    // Telefone com código do país (+55)
    if (!cleanPhoneNumber.startsWith('55')) {
      errors.push("Código do país inválido para telefone brasileiro");
    }
  }
  
  return { isValid: errors.length === 0, errors };
}

export function validateOptionalPhoneField(phone?: unknown): ValidationResult {
  if (typeof phone !== "string" || !phone.trim()) {
    return { isValid: true, errors: [] };
  }

  return validatePhone(phone);
}

// Validação de CPF/CNPJ
export function validateCpfCnpj(cpfCnpj: string): ValidationResult {
  const errors: string[] = [];
  
  if (!cpfCnpj || !cpfCnpj.trim()) {
    errors.push("CPF/CNPJ é obrigatório");
    return { isValid: false, errors };
  }
  
  // Remove todos os caracteres não numéricos
  const cleanCpfCnpj = cpfCnpj.replace(/\D/g, '');
  
  if (cleanCpfCnpj.length === 11) {
    // Validação de CPF
    if (!isValidCPF(cleanCpfCnpj)) {
      errors.push("CPF inválido");
    }
  } else if (cleanCpfCnpj.length === 14) {
    // Validação de CNPJ
    if (!isValidCNPJ(cleanCpfCnpj)) {
      errors.push("CNPJ inválido");
    }
  } else {
    errors.push("CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos");
  }
  
  return { isValid: errors.length === 0, errors };
}

// Validação de valor monetário
export function validateMoney(value: number | string): ValidationResult {
  const errors: string[] = [];
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) {
    errors.push("Valor deve ser um número válido");
    return { isValid: false, errors };
  }
  
  if (numValue < 0) {
    errors.push("Valor não pode ser negativo");
  }
  
  if (numValue === 0) {
    errors.push("Valor deve ser maior que zero");
  }
  
  return { isValid: errors.length === 0, errors };
}

// Validação de data
export function validateDate(date: string): ValidationResult {
  const errors: string[] = [];
  
  if (!date || !date.trim()) {
    errors.push("Data é obrigatória");
    return { isValid: false, errors };
  }
  
  // Evitar bug de timezone com "YYYY-MM-DD" (date-only)
  const dateObj = /^\d{4}-\d{2}-\d{2}$/.test(date) ? new Date(`${date}T00:00:00`) : new Date(date);
  
  if (isNaN(dateObj.getTime())) {
    errors.push("Data deve ter um formato válido");
    return { isValid: false, errors };
  }
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  if (dateObj < today) {
    errors.push("Data não pode ser no passado");
  }
  
  return { isValid: errors.length === 0, errors };
}

// Validação de nome
export function validateName(name: string): ValidationResult {
  const errors: string[] = [];
  
  if (!name || !name.trim()) {
    errors.push("Nome é obrigatório");
    return { isValid: false, errors };
  }
  
  if (name.trim().length < 2) {
    errors.push("Nome deve ter pelo menos 2 caracteres");
  }
  
  if (name.trim().length > 100) {
    errors.push("Nome deve ter no máximo 100 caracteres");
  }
  
  return { isValid: errors.length === 0, errors };
}

// Validação de quantidade
export function validateQuantity(quantity: number | string): ValidationResult {
  const errors: string[] = [];
  
  const numQuantity = typeof quantity === 'string' ? parseFloat(quantity) : quantity;
  
  if (isNaN(numQuantity)) {
    errors.push("Quantidade deve ser um número válido");
    return { isValid: false, errors };
  }
  
  if (numQuantity <= 0) {
    errors.push("Quantidade deve ser maior que zero");
  }
  
  if (!Number.isInteger(numQuantity)) {
    errors.push("Quantidade deve ser um número inteiro");
  }
  
  return { isValid: errors.length === 0, errors };
}

// Validação de descrição
export function validateDescription(description: string, maxLength: number = 500): ValidationResult {
  const errors: string[] = [];
  
  if (!description || !description.trim()) {
    errors.push("Descrição é obrigatória");
    return { isValid: false, errors };
  }
  
  if (description.trim().length < 5) {
    errors.push("Descrição deve ter pelo menos 5 caracteres");
  }
  
  if (description.trim().length > maxLength) {
    errors.push(`Descrição deve ter no máximo ${maxLength} caracteres`);
  }
  
  return { isValid: errors.length === 0, errors };
}

// Função auxiliar para validar CPF
function isValidCPF(cpf: string): boolean {
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) {
    return false;
  }
  
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cpf.charAt(i)) * (10 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.charAt(9))) return false;
  
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cpf.charAt(i)) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.charAt(10))) return false;
  
  return true;
}

// Função auxiliar para validar CNPJ
function isValidCNPJ(cnpj: string): boolean {
  if (cnpj.length !== 14 || /^(\d)\1{13}$/.test(cnpj)) {
    return false;
  }
  
  let sum = 0;
  let weight = 2;
  for (let i = 11; i >= 0; i--) {
    sum += parseInt(cnpj.charAt(i)) * weight;
    weight = weight === 9 ? 2 : weight + 1;
  }
  let remainder = sum % 11;
  const digit1 = remainder < 2 ? 0 : 11 - remainder;
  if (digit1 !== parseInt(cnpj.charAt(12))) return false;
  
  sum = 0;
  weight = 2;
  for (let i = 12; i >= 0; i--) {
    sum += parseInt(cnpj.charAt(i)) * weight;
    weight = weight === 9 ? 2 : weight + 1;
  }
  remainder = sum % 11;
  const digit2 = remainder < 2 ? 0 : 11 - remainder;
  if (digit2 !== parseInt(cnpj.charAt(13))) return false;
  
  return true;
}

// Validação combinada para formulários
export function validateForm(data: Record<string, unknown>, rules: Record<string, (value: unknown) => ValidationResult>): ValidationResult {
  const allErrors: string[] = [];
  let isValid = true;
  
  for (const [field, validator] of Object.entries(rules)) {
    const result = validator(data[field]);
    if (!result.isValid) {
      isValid = false;
      allErrors.push(...result.errors.map(error => `${field}: ${error}`));
    }
  }
  
  return { isValid, errors: allErrors };
}

// Validação de CPF/CNPJ
export function validateCpfCnpjField(cpfCnpj: string): ValidationResult {
  const errors: string[] = [];

  if (!cpfCnpj || !cpfCnpj.trim()) {
    errors.push("CPF/CNPJ é obrigatório");
    return { isValid: false, errors };
  }

  // Remove caracteres não numéricos para validação
  const cleanValue = cpfCnpj.replace(/[^\d]/g, '');

  if (cleanValue.length !== 11 && cleanValue.length !== 14) {
    errors.push("CPF/CNPJ deve ter 11 dígitos (CPF) ou 14 dígitos (CNPJ)");
    return { isValid: false, errors };
  }

  // Importar função de validação (vamos usar uma implementação inline para evitar dependências)
  const isValid = validateCpfCnpjInline(cleanValue);
  if (!isValid) {
    errors.push(cleanValue.length === 11 ? "CPF inválido" : "CNPJ inválido");
  }

  return { isValid: errors.length === 0, errors };
}

export function validateOptionalCpfCnpjField(cpfCnpj?: unknown): ValidationResult {
  if (typeof cpfCnpj !== "string" || !cpfCnpj.trim()) {
    return { isValid: true, errors: [] };
  }

  return validateCpfCnpjField(cpfCnpj);
}

// Função inline para validação de CPF/CNPJ (para evitar import circular)
function validateCpfCnpjInline(value: string): boolean {
  const cleanValue = value.replace(/[^\d]/g, '');

  if (cleanValue.length === 11) {
    return validateCpfInline(cleanValue);
  } else if (cleanValue.length === 14) {
    return validateCnpjInline(cleanValue);
  }

  return false;
}

function validateCpfInline(cpf: string): boolean {
  if (cpf.length !== 11) return false;

  // Verificar se todos os dígitos são iguais
  if (/^(\d)\1{10}$/.test(cpf)) return false;

  // Calcular primeiro dígito verificador
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cpf[i]) * (10 - i);
  }
  let remainder = (sum * 10) % 11;
  let digit1 = remainder === 10 || remainder === 11 ? 0 : remainder;

  // Calcular segundo dígito verificador
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cpf[i]) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  let digit2 = remainder === 10 || remainder === 11 ? 0 : remainder;

  return parseInt(cpf[9]) === digit1 && parseInt(cpf[10]) === digit2;
}

function validateCnpjInline(cnpj: string): boolean {
  if (cnpj.length !== 14) return false;

  // Verificar se todos os dígitos são iguais
  if (/^(\d)\1{13}$/.test(cnpj)) return false;

  // Calcular primeiro dígito verificador
  let sum = 0;
  let weight = 2;
  for (let i = 11; i >= 0; i--) {
    sum += parseInt(cnpj[i]) * weight;
    weight = weight === 9 ? 2 : weight + 1;
  }
  let remainder = sum % 11;
  let digit1 = remainder < 2 ? 0 : 11 - remainder;

  // Calcular segundo dígito verificador
  sum = 0;
  weight = 2;
  for (let i = 12; i >= 0; i--) {
    sum += parseInt(cnpj[i]) * weight;
    weight = weight + 1;
    if (weight === 10) weight = 2;
  }
  remainder = sum % 11;
  let digit2 = remainder < 2 ? 0 : 11 - remainder;

  return parseInt(cnpj[12]) === digit1 && parseInt(cnpj[13]) === digit2;
}
